import React, { useState } from 'react';
import { X, CheckCircle, Printer, ArrowDownLeft, Clock, Edit2, Truck } from 'lucide-react';
import { formatWarehouseDate } from '../../../../../utils/dateUtils';

const OffloadRequestProcessingModal = ({ request, isOpen, onClose, onComplete, onPrintPdf, onEditRequest, currentRole = 'WAREHOUSE_PERSON' }) => {
    const [warehouseRemarks, setWarehouseRemarks] = useState(request?.warehouseRemarks || 'All returned items received and verified.');
    const [itemsQty, setItemsQty] = useState(
        request?.items?.reduce((acc, item) => {
            acc[item.productId] = item.warehouseQty ?? item.qty;
            return acc;
        }, {}) || {}
    );

    if (!isOpen || !request) return null;

    const isCompleted = request.status === 'Completed' || request.status === 'Stock Received';
    const isSubmitted = request.status === 'Submitted' || request.status === 'Pending Return' || request.status === 'Draft';

    const handleQtyChange = (productId, val) => {
        const num = Math.max(0, Number(val) || 0);
        setItemsQty(prev => ({ ...prev, [productId]: num }));
    };

    const handleComplete = () => {
        onComplete(request.id, {
            warehouseRemarks,
            completedBy: 'Warehouse Staff',
            itemsQty
        });
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
            <div className="bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl border border-slate-200 dark:border-slate-800 shadow-2xl w-full max-w-4xl flex flex-col max-h-[calc(100vh-100px)] sm:max-h-[92vh] overflow-hidden my-auto">
                
                {/* Header (Aligned Theme with Loading Request Processing Modal) */}
                <div className="p-4 sm:p-5 border-b border-slate-200/80 dark:border-slate-700 flex items-center justify-between bg-white dark:bg-slate-800 shrink-0">
                    <div className="flex items-center gap-3 min-w-0 flex-1 mr-2">
                        <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold shrink-0">
                            <ArrowDownLeft className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h2 className="text-base sm:text-lg font-black text-slate-800 dark:text-white leading-tight flex items-center gap-2 flex-wrap">
                                {request.requestNo}
                                <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${
                                    isSubmitted ? 'bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300' :
                                    isCompleted ? 'bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300' :
                                    'bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300'
                                }`}>
                                    {request.status}
                                </span>
                            </h2>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 text-xs text-slate-500 dark:text-slate-400 mt-1">
                                <span className="truncate">
                                    Salesperson: <strong className="font-semibold text-slate-700 dark:text-slate-300">{request.salesperson}</strong>
                                </span>
                                <span className="hidden sm:inline text-slate-300 dark:text-slate-600">|</span>
                                <span className="inline-flex items-center gap-1 font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/40 px-2 py-0.5 rounded-md border border-blue-100 dark:border-blue-800/60 w-fit text-[11px] sm:bg-transparent sm:dark:bg-transparent sm:border-0 sm:p-0 sm:text-xs sm:font-semibold sm:text-slate-700 sm:dark:text-slate-300">
                                    <Truck className="w-3 h-3 text-blue-500 shrink-0" />
                                    <span>Vehicle: {request.vehicle || 'Van 01'}</span>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            type="button"
                            onClick={() => onPrintPdf(request)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                        >
                            <Printer className="w-4 h-4" /> Print PDF
                        </button>
                        <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-full">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Body Content */}
                <div className="p-4 sm:p-6 space-y-5 flex-1 min-h-0 overflow-y-auto">
                    
                    {/* Status Notice Banner for Pending Offload */}
                    {isSubmitted && (
                        <div className="p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-2xl flex items-center gap-3 text-xs text-amber-800 dark:text-amber-300">
                            <Clock className="w-5 h-5 flex-shrink-0 text-amber-600" />
                            <div>
                                <strong className="font-bold block text-sm">Offload Request Pending Verification</strong>
                                Review returned stock items below, verify quantities, and complete stock receipt.
                            </div>
                        </div>
                    )}

                    {/* Readonly Request Information Header */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 dark:bg-slate-700/30 rounded-2xl border border-slate-100 dark:border-slate-700 text-xs">
                        <div>
                            <span className="text-slate-400 block font-semibold text-[10px] uppercase">Vehicle</span>
                            <span className="font-bold text-slate-800 dark:text-white text-sm">{request.vehicle || 'Van 01'}</span>
                        </div>
                        <div>
                            <span className="text-slate-400 block font-semibold text-[10px] uppercase">Warehouse</span>
                            <span className="font-bold text-slate-800 dark:text-white">{request.warehouse || 'Main Warehouse'}</span>
                        </div>
                        <div>
                            <span className="text-slate-400 block font-semibold text-[10px] uppercase">Salesperson</span>
                            <span className="font-bold text-slate-800 dark:text-white">{request.salesperson}</span>
                        </div>
                        <div>
                            <span className="text-slate-400 block font-semibold text-[10px] uppercase">Request Date</span>
                            <span className="font-bold text-slate-800 dark:text-white">{formatWarehouseDate(request.date || request.requestDate)}</span>
                        </div>
                        {request.remarks && (
                            <div className="col-span-2 md:col-span-4 pt-2 border-t border-slate-200 dark:border-slate-600">
                                <span className="text-slate-400 block font-semibold text-[10px] uppercase">Offload Reason Remarks</span>
                                <span className="italic text-slate-700 dark:text-slate-300">{request.remarks}</span>
                            </div>
                        )}
                    </div>

                    {/* Stock Items Verification Table Section */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <h3 className="font-bold text-xs uppercase tracking-wider text-slate-500">
                                Offload Line Items ({request.items?.length || 0} Products)
                            </h3>
                            {/* Mobile Horizontal Scroll Indicator */}
                            <span className="text-[10px] text-blue-600 dark:text-blue-400 font-bold sm:hidden flex items-center gap-1 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-md border border-blue-100 dark:border-blue-800">
                                ↔ Scroll table horizontally
                            </span>
                        </div>

                        <div className="border border-slate-200 dark:border-slate-700 rounded-2xl overflow-x-auto custom-scrollbar-blue pb-2">
                            <table className="w-full text-left text-xs min-w-[550px]">
                                <thead className="bg-slate-50 dark:bg-slate-700/50 text-slate-500 uppercase font-bold border-b border-slate-200 dark:border-slate-700">
                                    <tr>
                                        <th className="p-3.5 pl-4">Product Description</th>
                                        <th className="p-3.5 text-center">Returned Qty</th>
                                        <th className="p-3.5 text-center">Verified Qty</th>
                                        <th className="p-3.5">Unit</th>
                                        <th className="p-3.5">Return Reason</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700 font-medium">
                                    {(request.items || []).map((item) => (
                                        <tr key={item.id || item.productId} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/20">
                                            <td className="p-3.5 pl-4 font-bold text-slate-800 dark:text-white min-w-[160px]">
                                                {item.productName || item.name}
                                            </td>
                                            <td className="p-3.5 text-center text-blue-600 dark:text-blue-400 font-bold whitespace-nowrap">
                                                {item.qty} {item.unit || 'pcs'}
                                            </td>
                                            <td className="p-3.5 text-center">
                                                {!isCompleted ? (
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        value={itemsQty[item.productId] ?? item.qty}
                                                        onChange={(e) => handleQtyChange(item.productId, e.target.value)}
                                                        className="w-20 px-2 py-1 text-center font-bold bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-800 dark:text-white focus:ring-2 focus:ring-blue-500"
                                                    />
                                                ) : (
                                                    <span className="font-bold text-blue-600 dark:text-blue-400 whitespace-nowrap">
                                                        {item.warehouseQty ?? item.qty} {item.unit || 'pcs'}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="p-3.5 text-slate-500 whitespace-nowrap">{item.unit || 'pcs'}</td>
                                            <td className="p-3.5 text-slate-600 dark:text-slate-300 text-[11px] font-semibold min-w-[130px]">
                                                <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600">
                                                    {item.reason || 'Return'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Warehouse Staff Verification Remarks */}
                    <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                        <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                            Warehouse Stock Receipt Remarks
                        </label>
                        <textarea
                            rows="2"
                            value={warehouseRemarks}
                            disabled={isCompleted}
                            onChange={(e) => setWarehouseRemarks(e.target.value)}
                            placeholder="Add offload verification remarks or shelf location details..."
                            className="w-full p-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-xs text-slate-800 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                        ></textarea>
                    </div>
                </div>

                {/* Footer Action Buttons (Identical Layout to Loading Request Processing Modal) */}
                <div className="p-3 sm:p-4 px-4 sm:px-6 bg-white dark:bg-slate-800 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between shadow-lg shrink-0 sticky bottom-0 z-20 pb-safe pb-3 sm:pb-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white"
                    >
                        Close
                    </button>

                    <div className="flex items-center gap-3">
                        {!isCompleted ? (
                            <div className="flex items-center gap-2">
                                {onEditRequest && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onClose();
                                            onEditRequest(request);
                                        }}
                                        className="px-4 py-2.5 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-100 rounded-xl text-xs font-bold border border-amber-200 dark:border-amber-800 flex items-center gap-1.5 cursor-pointer"
                                    >
                                        <Edit2 className="w-4 h-4" /> Edit Request
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={handleComplete}
                                    className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-500/20 flex items-center gap-1.5 cursor-pointer"
                                >
                                    <CheckCircle className="w-4 h-4" /> Complete Offload
                                </button>
                            </div>
                        ) : (
                            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1">
                                <CheckCircle className="w-4 h-4" /> Offload Completed
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OffloadRequestProcessingModal;
